import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { projectsApi } from "@/services/projectsApi";
import { auditLogApi } from "@/services/auditLogApi";

interface ProjectPromptLibrary {
  id: string;
  source_prompt_library_id: string;
  name: string;
  priming: string | null;
  ordinal: number | null;
}

interface ProjectTemplateFormProps {
  onClose: () => void;
  activeTemplate: {
    template: { source_template_id: string; name: string; description: string | null };
    prompt_libraries: ProjectPromptLibrary[];
  };
  projectId: string;
  projectType?: string;
  allTemplates?: { id: string; name: string; status?: string }[];
  selectedTemplateId?: string;
  onSelectTemplate?: (id: string) => void;
}

interface FormField {
  id: string;
  source_prompt_library_id: string;
  label: string;
  question: string;
  ordinal: number;
}

interface FormValues {
  fields: FormField[];
}

const ProjectTemplateForm = ({ onClose, activeTemplate, projectId, projectType, allTemplates, selectedTemplateId, onSelectTemplate }: ProjectTemplateFormProps) => {
  const qc = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [populationError, setPopulationError] = useState(false);

  const { control, handleSubmit, reset, getValues } = useForm<FormValues>({
    defaultValues: { fields: [] },
  });

  const { fields } = useFieldArray({ control, name: "fields", keyName: "_id" });
  const lastResetTemplateIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (activeTemplate.template.source_template_id === lastResetTemplateIdRef.current) return;
    lastResetTemplateIdRef.current = activeTemplate.template.source_template_id;
    const sorted = [...activeTemplate.prompt_libraries].sort(
      (a, b) => (a.ordinal ?? Infinity) - (b.ordinal ?? Infinity)
    );
    reset({
      fields: sorted.map((pl) => ({
        id: pl.id,
        source_prompt_library_id: pl.source_prompt_library_id,
        label: pl.name,
        question: pl.priming ?? "",
        ordinal: pl.ordinal ?? 0,
      })),
    });
  }, [activeTemplate, reset]);

  const logPromptEdits = (
    dirtyFields: FormField[],
    originalMap: Map<string, { name: string; priming: string }>
  ) =>
    Promise.all(
      dirtyFields.map((f) =>
        auditLogApi.recordAuditEvent({
          projectId,
          entityType: "project_prompt_library",
          entityId: f.id,
          entityName: f.label,
          action: "edit_prompt",
          stage: "Extraction",
          before: JSON.stringify(originalMap.get(f.id)),
          after: JSON.stringify({ name: f.label, priming: f.question }),
        })
      )
    );

  const save = async (data: FormValues): Promise<boolean> => {
    if (projectType?.toUpperCase() === "PICO" && data.fields[0]?.label?.toLowerCase() !== "population") {
      setPopulationError(true);
      return false;
    }
    setPopulationError(false);

    const originalMap = new Map(
      activeTemplate.prompt_libraries.map((pl) => [pl.id, { name: pl.name, priming: pl.priming ?? "" }])
    );
    const dirtyFields = data.fields.filter((f) => {
      const orig = originalMap.get(f.id);
      return orig && (orig.name !== f.label || orig.priming !== f.question);
    });

    setIsSaving(true);
    try {
      if (dirtyFields.length > 0) {
        await Promise.all(
          dirtyFields.map((f) =>
            projectsApi.updateProjectPromptLibrary(projectId, f.id, {
              name: f.label,
              priming: f.question,
            })
          )
        );
        qc.invalidateQueries({ queryKey: ["projects", projectId, "templates", "active-with-prompts"] });
        void logPromptEdits(dirtyFields, originalMap);
      }
      return true;
    } catch {
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const autoSaveCurrentTab = () => {
    const data = getValues();
    const originalMap = new Map(
      activeTemplate.prompt_libraries.map((pl) => [pl.id, { name: pl.name, priming: pl.priming ?? "" }])
    );
    const dirtyFields = data.fields.filter((f) => {
      const orig = originalMap.get(f.id);
      return orig && (orig.name !== f.label || orig.priming !== f.question);
    });
    if (dirtyFields.length === 0) return;
    Promise.all(
      dirtyFields.map((f) =>
        projectsApi.updateProjectPromptLibrary(projectId, f.id, { name: f.label, priming: f.question })
      )
    ).then(() => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "templates", "active-with-prompts"] });
      void logPromptEdits(dirtyFields, originalMap);
    }).catch((err) => {
      console.error("Auto-save failed on tab switch:", err);
    });
  };

  const submitSave = handleSubmit(async (data) => {
    if (!await save(data)) return;
    onClose();
  });

  return (
    <form onSubmit={submitSave} className="grow min-h-0 flex flex-col">
      {allTemplates && allTemplates.length > 1 && selectedTemplateId && onSelectTemplate && (
        <Tabs value={selectedTemplateId} onValueChange={(id) => { autoSaveCurrentTab(); onSelectTemplate?.(id); }} className="shrink-0 border-b bg-white">
          <TabsList className="mt-1 flex-wrap" variant="line">
            {allTemplates.map(t => (
              <TabsTrigger key={t.id} value={t.id}>
                {t.name}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
      <div className="grow overflow-y-auto space-y-6 pb-6">
        {/* Read-only template metadata */}
        <div className="p-4 bg-gray-100 border-b shrink-0">
          <div className="space-y-1">
            <h2 className="text-lg font-bold text-foreground">{activeTemplate.template.name}</h2>
          </div>
          {activeTemplate.template.description && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Description</p>
              <p className="text-sm text-muted-foreground">{activeTemplate.template.description}</p>
            </div>
          )}
        </div>

        {/* Editable columns */}
        <div className="grow space-y-4 px-4 ">
          <h3 className="font-semibold">Columns</h3>

          {fields.map((field, index) => (
            <div key={field._id} className="space-y-1.5">
              <p className="text-sm font-semibold text-foreground">{field.label}</p>
              <Controller
                control={control}
                name={`fields.${index}.question`}
                render={({ field: f }) => (
                  <Textarea {...f} placeholder="What question should be asked?" className="min-h-[100px]" />
                )}
              />
            </div>
          ))}
        </div>

        {/* Population-first validation warning */}
        {populationError && (
          <p className="text-sm text-destructive rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2">
            The first column must be &ldquo;Population&rdquo;. Please update the template column order before saving.
          </p>
        )}
      </div>
      {/* Footer */}
      <div className="bg-white border-t shrink-0 p-4 flex gap-3">
        <Button type="button" variant="outline" className="flex-1" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="submit" variant="brand" className="flex-1" disabled={isSaving || populationError}>
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
};

export default ProjectTemplateForm;
